import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UrlEnum } from '../../enum/url.enum';
import { IBookmarkRequest } from '../../interface/dashboard/bookmark.interface';
import {
  annualReportModel,
  ngoAwardModel,
} from '../../model/annual-report.model';
import { ClaimNgoModel } from '../../model/claim-ngo.model';
import { ColumnSearchRequest } from '../../model/column-search-request.model';
import { NgoModel } from '../../model/register-new-ngo.model';
import { FileService } from '../file/file.service';
import { HttpRequestService } from '../http-request/http-request.service';
import { UtilitiesService } from '../utilities/utilities.service';

@Injectable({
  providedIn: 'root',
})
export class NgoService {
  private elasticsearchDocumentAPI = `${UrlEnum.SHOHAY_ELASTIC}/document`;
  private ngoAPI = `${UrlEnum.SHOHAY_NGO}/ngo-profile`;
  private bookmarkApi = UrlEnum.SHOHAY_NGO + `/bookmark`;
  private sdgApi = UrlEnum.SHOHAY_NGO + `/sdg/get-campaign-sdg`;
  private causeAreaApi = UrlEnum.SHOHAY_NGO + `/cause-area/get-ngo-cause-area`;
  private geoLocationApi =
    UrlEnum.SHOHAY_NGO + `/working-location/division-district-upazila`;
  private geoUnionApi = UrlEnum.SHOHAY_NGO + `/working-location/union`;

  private indexName: string;
  constructor(
    private readonly fileService: FileService,
    private readonly utilitiesService: UtilitiesService,
    private readonly httpRequestService: HttpRequestService
  ) {
    this.indexName = environment.envName === 'stage' ? 'ngo_from_demo' : 'ngo';
  }

  public getAllVerifiedNgos(): Observable<any> {
    return this.httpRequestService
      .get(`${this.ngoAPI}/get-all-ngo?is_verified=true`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any) =>
          x.sort(
            this.utilitiesService.dynamicSortWithDate('updated_at', 'desc')
          )
        ),
        map((x: any[]) =>
          x.map((ngo: any) => ({
            ...ngo,
            logo_url: this.utilitiesService.jsonParse(ngo?.logo_url),
          }))
        )
      );
  }

  public getNgoById(ngoId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.ngoAPI}/get-by-id/${ngoId}`)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getNgoDetails(
    ngoId: string,
    startDate: Date,
    endDate: Date
  ): Observable<any> {
    return this.httpRequestService
      .get(
        `${this.ngoAPI}/get-dashboard-by-ngoid/${ngoId}?startDate=${startDate}&endDate=${endDate}`
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  ////// Elastic API //////
  public searchNgo(query: string): Observable<any> {
    return this.httpRequestService
      .get(
        `${this.elasticsearchDocumentAPI}/search?index_name=${this.indexName}&query=${query}`,
        { skipAuth: true }
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public searchNgoByColumn(body: ColumnSearchRequest): Observable<any> {
    const url = `${this.elasticsearchDocumentAPI}/search-by-column`;
    body.index_name = this.indexName;
    return this.httpRequestService
      .post(url, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getNgoWithPagination(
    page: number,
    indexName: string = 'ngo_from_demo'
  ): Observable<any> {
    const url = `${this.elasticsearchDocumentAPI}/all`;
    const params = new HttpParams()
      .set('index_name', this.indexName)
      .set('page', page.toString());

    return this.httpRequestService
      .get<any>(url, {
        params,
        skipAuth: true,
      })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getNgoByElasticId(elasticId: string): Observable<any> {
    return this.httpRequestService
      .get(
        `${this.elasticsearchDocumentAPI}/details/${elasticId}/${this.indexName}`,
        {
          skipAuth: true,
        }
      )
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }
  ////// Elastic API End //////

  ////// NGO By User //////
  public getAllNgoByUserId(userId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.ngoAPI}/get-by-user/${userId}`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any[]) =>
          x.map((ngo: any) => ({
            ...ngo,
            logo_url: this.utilitiesService.jsonParse(ngo?.logo_url),
          }))
        )
      );
  }

  public getNgoByUserId(userId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.ngoAPI}/get-by-user/${userId}`)
      .pipe(
        map((x: any) => this.utilitiesService.responseHandler(x)),
        map((x: any) =>
          x.sort(this.utilitiesService.dynamicSortWithDate('created_at', 'asc'))
        ),
        map((x: any) => this.utilitiesService.getSlNo(x)),
        map((x: any[]) =>
          x.map((ngo: any) => ({
            ...ngo,
            logo_url: this.utilitiesService.jsonParse(ngo?.logo_url),
          }))
        )
      );
  }

  public registerNewNgo(body: NgoModel): Observable<any> {
    return this.httpRequestService
      .post(this.ngoAPI, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public claimNgo(body: ClaimNgoModel): Observable<any> {
    const url = `${this.ngoAPI}/claim`;
    return this.httpRequestService
      .post(url, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public createAnnualReport(body: annualReportModel): Observable<any> {
    const url = `${this.ngoAPI}/create-report`;
    return this.httpRequestService
      .post(url, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getAnnualReports(ngoId: string): Observable<any> {
    const url = `${this.ngoAPI}/get-ngo-report/${ngoId}`;
    return this.httpRequestService
      .get(url, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getAwardList(ngoId: string): Observable<any> {
    const url = `${this.ngoAPI}/get-ngo-award/${ngoId}`;
    return this.httpRequestService
      .get(url, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public createAward(body: ngoAwardModel): Observable<any> {
    const url = `${this.ngoAPI}/create-award`;
    return this.httpRequestService
      .post(url, body, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public updateNgoProfile(
    id: string,
    body: NgoModel,
    imgFile: File | null = null,
    imgProperty: string | null = null
  ): Observable<any> {
    return this.fileService.uploadSingleFile(imgFile).pipe(
      switchMap((imgResponse: any) => {
        if (imgResponse && imgResponse?.length > 0) {
          body[imgProperty] = JSON.stringify(imgResponse[0]);
        }
        return this.httpRequestService
          .patch(`${this.ngoAPI}/${id}`, body)
          .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  public ngoWithBookmarIds(documentIds: string[]) {
    if (!documentIds || documentIds.length === 0) {
      console.error('No document IDs provided');
      return of([]);
    }
    const url = `${this.elasticsearchDocumentAPI}/get-by-ids`;
    const documentIdsParam =
      documentIds.length === 1 ? documentIds[0] : documentIds.join(',');
    const params = new HttpParams()
      .set('index_name', 'ngo_from_demo')
      .set('document_Ids', documentIdsParam);

    return this.httpRequestService
      .get<any>(url, { params })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public createBookmark(bookmark: IBookmarkRequest): Observable<any> {
    return this.httpRequestService.post(this.bookmarkApi, bookmark);
  }

  public getBookmarkById(userId: string): Observable<any> {
    return this.httpRequestService.get(`${this.bookmarkApi}/${userId}`);
  }

  public deleteBookmarkById(id: string): Observable<any> {
    return this.httpRequestService.delete(`${this.bookmarkApi}/${id}`);
  }
  ////// NGO By User End //////

  public getCauseAreaList(): Observable<any> {
    return this.httpRequestService
      .get(this.causeAreaApi, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getGeoLocationList(): Observable<any> {
    return this.httpRequestService
      .get(this.geoLocationApi, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getUnionByUpazilaId(upazilaId: string): Observable<any> {
    return this.httpRequestService
      .get(`${this.geoUnionApi}/${upazilaId}`, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getCampaignSdg(): Observable<any> {
    return this.httpRequestService
      .get(this.sdgApi, { skipAuth: true })
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
    // return of([
    //   {
    //     id: '6cea4cb9-fbb2-4a92-855a-f96d2d517c3f',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Take urgent action to combat climate change and its impacts.',
    //     is_active: true,
    //     sl_no: 13,
    //     title: 'Climate Action',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: 'a584ed60-6b83-402a-b887-9f06c8e04ae0',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Promote sustained, inclusive, and sustainable economic growth, full and productive employment, and decent work for all.',
    //     is_active: true,
    //     sl_no: 8,
    //     title: 'Decent Work and Economic Growth',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '5f19ebb7-8648-41ec-9c34-325b79348d47',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Build resilient infrastructure, promote inclusive and sustainable industrialization, and foster innovation.',
    //     is_active: true,
    //     sl_no: 9,
    //     title: 'Industry, Innovation and Infrastructure',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '1fdffbe7-daa3-42d6-bc8d-b9ce43a97e11',
    //     created_at: null,
    //     created_by: null,
    //     description: 'Reduce inequality within and among countries.',
    //     is_active: true,
    //     sl_no: 10,
    //     title: 'Reduced Inequalities',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '2c067478-76ba-4858-9756-b54254318943',
    //     created_at: null,
    //     created_by: null,
    //     description: 'Ensure sustainable consumption and production patterns.',
    //     is_active: true,
    //     sl_no: 12,
    //     title: 'Responsible Consumption and Production',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '96a97301-a366-4517-be48-6823225d07ba',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Ensure availability and sustainable management of water and sanitation for all.',
    //     is_active: true,
    //     sl_no: 6,
    //     title: 'Clean Water and Sanitation',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '750b3245-8661-4bb8-b71a-3ac991df94c6',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Strengthen the means of implementation and revitalize the global partnership for sustainable development.',
    //     is_active: true,
    //     sl_no: 17,
    //     title: 'Partnerships for the Goals',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: 'b1d39c57-68ac-4d29-8e2c-815640370c7a',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Ensure healthy lives and promote well-being for all at all ages.',
    //     is_active: true,
    //     sl_no: 3,
    //     title: 'Good Health and Well-being',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: 'f1ea9680-2072-4939-a950-8f958b5a640e',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Protect, restore, and promote sustainable use of terrestrial ecosystems.',
    //     is_active: true,
    //     sl_no: 15,
    //     title: 'Life on Land',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '4c181ab0-72ae-441a-98bf-6fce9afb7181',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Conserve and sustainably use the oceans, seas, and marine resources.',
    //     is_active: true,
    //     sl_no: 14,
    //     title: 'Life Below Water',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '74ad1179-1125-4207-810c-c10fcea35403',
    //     created_at: null,
    //     created_by: null,
    //     description: 'Achieve gender equality and empower all women and girls.',
    //     is_active: true,
    //     sl_no: 5,
    //     title: 'Gender Equality',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: 'ff964f33-351f-4777-bbce-69610f5f0f12',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'End hunger, achieve food security and improved nutrition, and promote sustainable agriculture.',
    //     is_active: true,
    //     sl_no: 2,
    //     title: 'Zero Hunger',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '604d8a8d-36fe-4be7-a560-d269e0a8923b',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Make cities and human settlements inclusive, safe, resilient, and sustainable.',
    //     is_active: true,
    //     sl_no: 11,
    //     title: 'Sustainable Cities and Communities',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '563aaf9f-81ac-4b9e-acba-d398915289fe',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Ensure inclusive and equitable quality education and promote lifelong learning opportunities.',
    //     is_active: true,
    //     sl_no: 4,
    //     title: 'Quality Education',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '83154324-3fed-47e3-9071-aa246d82ea0f',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Ensure access to affordable, reliable, sustainable, and modern energy.',
    //     is_active: true,
    //     sl_no: 7,
    //     title: 'Affordable and Clean Energy',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: '91557cd3-ae98-4e59-8c53-2e2f92b345f5',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Promote peaceful and inclusive societies, provide access to justice, and build accountable institutions.',
    //     is_active: true,
    //     sl_no: 16,
    //     title: 'Peace, Justice and Strong Institutions',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    //   {
    //     id: 'a5fe2a36-0335-4c2a-8030-44a450507a85',
    //     created_at: null,
    //     created_by: null,
    //     description:
    //       'Strengthen the means of implementation and revitalize the global partnership for sustainable development.',
    //     is_active: true,
    //     sl_no: 17,
    //     title: 'Partnerships for the Goals',
    //     updated_at: null,
    //     updated_by: null,
    //   },
    // ]);
  }
}
